import type { ReactNode } from "react";
import { Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ClassificationBadge } from "@/components/ui/classification-badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RiskLevelBadge } from "@/components/ui/risk-level-badge/RiskLevelBadge";
import type { ClassificationType } from "@/types/entity";
import { formatDataOra, formatNumar } from "@/utils/targetareFormatting";
import type { RandActivitate } from "@/utils/targetareRaportActivitate";
import { riskLevelDinNivel } from "@/utils/targetareRisc";
import type { ColoanaTabel } from "@/utils/targetareTabel";
import { TierBadge } from "../TierBadge";

export type ContextActivitate = {
  onFisa: (cui: string) => void;
  onAdaugaTinta: (cui: string) => void;
  adaugaInCurs: boolean;
};

const nivelDinAplicatie = (nivel: string | null) => {
  switch (nivel) {
    case "de BAZĂ":
      return "de_baza";
    case "IMPORTANT":
      return "important";
    case "ESENȚIAL":
      return "esential";
    default:
      return null;
  }
};

function Sesiuni({ firma }: { firma: RandActivitate }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="text-left text-xs hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {formatNumar(firma.nr_sesiuni)}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[440px] p-2"
        onClick={(e) => e.stopPropagation()}
      >
        <ul className="max-h-72 space-y-1 overflow-auto text-xs">
          {firma.sesiuni.map((s) => (
            <li key={s.id} className="rounded border px-2 py-1">
              <p>
                <span className="font-medium">
                  {formatDataOra(s.actualizat)}
                </span>
                {" · "}
                {s.stare}
                {s.pas !== null && ` · pasul ${s.pas}`}
                {s.finalizat && " · finalizată"}
              </p>
              <p className="text-muted-foreground">
                {s.utilizator ?? "utilizator necunoscut"}
                {s.incadrare && ` · încadrare: ${s.incadrare}`}
                {s.nivel_risc && ` · risc: ${s.nivel_risc}`}
                {s.scor_risc !== null && ` (${formatNumar(s.scor_risc)})`}
              </p>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

export function celulaActivitate(ctx: ContextActivitate) {
  return (
    coloana: ColoanaTabel<RandActivitate>,
    r: RandActivitate
  ): ReactNode => {
    const v = r[coloana.id];
    switch (coloana.id) {
      case "denumire":
        return (
          <button
            type="button"
            className="max-w-full truncate text-left font-medium hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              ctx.onFisa(r.cui);
            }}
            title={r.denumire ?? ""}
          >
            {r.denumire ?? "(fără denumire)"}
          </button>
        );
      case "cui":
        return <span className="font-mono text-xs">{r.cui}</span>;
      case "utilizatori":
        return (
          <span className="block font-mono text-xs">
            {r.utilizatori.length === 0
              ? "–"
              : r.utilizatori.map((u) => (
                  <span key={u} className="block">
                    {u}
                  </span>
                ))}
          </span>
        );
      case "nr_sesiuni":
        return <Sesiuni firma={r} />;
      case "ultima_activitate":
      case "prima_activitate":
        return (
          <span className="text-xs">{formatDataOra(v as string | null)}</span>
        );
      case "incadrare":
        return r.incadrare ? (
          <ClassificationBadge
            classification={r.incadrare as ClassificationType}
            size="sm"
          />
        ) : (
          <span className="text-xs text-muted-foreground">–</span>
        );
      case "nivel_risc":
        return r.nivel_risc ? (
          <RiskLevelBadge
            riskLevel={riskLevelDinNivel(nivelDinAplicatie(r.nivel_risc))}
          />
        ) : (
          <span className="text-xs text-muted-foreground">–</span>
        );
      case "dimensiune":
        return r.dimensiune ? (
          <Badge variant="outline" className="font-normal">
            {r.dimensiune}
          </Badge>
        ) : (
          ""
        );
      case "tier":
        return <TierBadge tier={r.tier} />;
      case "clasificare_estimata":
        return (
          <ClassificationBadge
            classification={r.clasificare_estimata}
            size="sm"
          />
        );
      case "nivel_risc_estimat":
        return r.nivel_risc_estimat ? (
          <RiskLevelBadge riskLevel={riskLevelDinNivel(r.nivel_risc_estimat)} />
        ) : (
          ""
        );
      case "tinta_text":
        return r.tinta_stare ? (
          <Badge variant="secondary" className="font-normal">
            {r.tinta_text}
          </Badge>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="h-7"
            disabled={ctx.adaugaInCurs}
            onClick={(e) => {
              e.stopPropagation();
              ctx.onAdaugaTinta(r.cui);
            }}
          >
            <Plus className="mr-1 h-3 w-3" />
            Adaugă la ținte
          </Button>
        );
      default:
        break;
    }
    if (Array.isArray(v)) return v.join(", ");
    if (typeof v === "boolean") return v ? "da" : "nu";
    return (
      <span title={String(v ?? "")} className="block truncate">
        {String(v ?? "")}
      </span>
    );
  };
}
